import { useState } from "react";
import { X, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

const TestEmailModal = ({ isOpen, onClose, defaultTo = "" }) => {
  const [to, setTo] = useState(defaultTo);
  const [mailbox, setMailbox] = useState("info");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, from, to } | { error }

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/test-email", { to, mailbox });
      setResult({ ok: true, ...data });
    } catch (err) {
      setResult({ error: err.response?.data?.detail || "Send failed" });
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setResult(null);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="bg-white w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 bg-[#0B2343] text-white">
          <h3 className="font-serif font-bold flex items-center gap-2">
            <Send size={16} /> Send Test Email
          </h3>
          <button
            onClick={close}
            data-testid="test-email-close"
            className="text-slate-300 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Send From
            </label>
            <select
              value={mailbox}
              onChange={(e) => setMailbox(e.target.value)}
              data-testid="test-email-mailbox"
              className="w-full border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#C89B3C]"
            >
              <option value="info">info@noblestrategiesconsulting.com (Consulting / Education)</option>
              <option value="mvega">mvega@noblestrategiesconsulting.com (Insurance)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
              Send To <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="test-email-to"
              className="w-full border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#C89B3C]"
              placeholder="you@example.com"
            />
          </div>

          {result?.ok && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm p-3 flex items-start gap-2">
              <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Sent!</strong> Check {result.to} in a few seconds.
              </div>
            </div>
          )}
          {result?.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div className="whitespace-pre-wrap">{result.error}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={close}
              className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={sending}
              data-testid="test-email-submit"
              className="bg-[#C89B3C] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#a8852f] transition-colors disabled:opacity-50"
            >
              <Send size={16} />
              {sending ? "Sending..." : "Send Test"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestEmailModal;
